// ---------------------------------------------------------------------------
// ฟีเจอร์ custom (ตามคำขอลูกค้า) — logic ล้วน เทสต์ได้
// ---------------------------------------------------------------------------
// flow: ร้านกรอกคำขอ (pending_quote) -> ทีมงานกรอกใบเสนอราคาในชีต custom_requests
//       (mandays, ratePerManday, amount, quoteNote, quotedAt) แล้วตั้ง status=quoted
//       -> ร้านจ่าย (หรือใส่ตะกร้า) -> paid -> ทีมงานเปลี่ยน in_progress / done ในชีต
// หน้าเว็บตรวจใบเสนอราคาก่อนให้จ่ายทุกครั้ง ถ้าไม่ผ่านไม่ให้จ่าย
// ---------------------------------------------------------------------------

export const CUSTOM_MIN_RATE = 4000; // บาท / manday ขั้นต่ำ
export const CUSTOM_KIND = "custom";
export const API_ACCESS_KIND = "api_access";

export const CUSTOM_STATUS = {
  pending_quote: { th: "รอเสนอราคา", en: "Awaiting quote", tone: "amber" },
  quoted: { th: "รอชำระเงิน", en: "Quoted · awaiting payment", tone: "indigo" },
  paid: { th: "ชำระแล้ว รอเริ่มงาน", en: "Paid · queued", tone: "sky" },
  in_progress: { th: "กำลังดำเนินการ", en: "In progress", tone: "sky" },
  done: { th: "เสร็จสิ้น", en: "Done", tone: "green" },
  cancelled: { th: "ยกเลิก", en: "Cancelled", tone: "red" },
};

export const API_ACCESS_STATUS = {
  pending_quote: { th: "รอทีมงานติดต่อกลับ", en: "Waiting for our team", tone: "amber" },
  in_progress: { th: "กำลังดำเนินการ", en: "In progress", tone: "sky" },
  done: { th: "ออก credential แล้ว", en: "Credentials issued", tone: "green" },
  cancelled: { th: "ยกเลิก", en: "Cancelled", tone: "red" },
};

export const INVALID_QUOTE_MESSAGE = "ใบเสนอราคาไม่ถูกต้อง กรุณาติดต่อทีมงาน";
export const INVALID_QUOTE_MESSAGE_EN = "This quotation is invalid. Please contact our team.";

/** ยอดที่ถูกต้อง = mandays × ratePerManday ปัดถึงบาท */
export const expectedQuoteAmount = (mandays, rate) => Math.round(Number(mandays) * Number(rate));

/**
 * ตรวจใบเสนอราคา — { ok, reason }
 *   reason: incomplete (ข้อมูลไม่ครบ) | rate (ต่ำกว่าขั้นต่ำ) | amount (ยอดไม่ตรงสูตร)
 */
export const validateQuote = (request) => {
  const mandays = Number(request?.mandays);
  const rate = Number(request?.ratePerManday);
  const amount = Number(request?.amount);
  if (!(mandays > 0) || !Number.isFinite(mandays) || !(rate > 0) || !Number.isFinite(rate) || !(amount > 0) || !Number.isFinite(amount)) {
    return { ok: false, reason: "incomplete" };
  }
  if (rate < CUSTOM_MIN_RATE) return { ok: false, reason: "rate" };
  if (amount !== expectedQuoteAmount(mandays, rate)) return { ok: false, reason: "amount" };
  return { ok: true, reason: "", amount, mandays, rate };
};

/** จ่ายได้เมื่ออยู่สถานะ quoted และใบเสนอราคาผ่านการตรวจ */
export const quotePayable = (request) => {
  if (!request || request.kind === API_ACCESS_KIND || request.status !== "quoted") return { ok: false, reason: "status" };
  return validateQuote(request);
};

/** ร้านยกเลิกเองได้เฉพาะก่อนจ่าย */
export const canCancelRequest = (request) => !!request && (request.status === "pending_quote" || request.status === "quoted");

export const LINK_MAX = 5;
export const ATTACH_MAX_BYTES = 25 * 1024; // ไฟล์แนบเล็ก — ชีตเก็บได้ไม่เกิน ~50,000 ตัวอักษรต่อเซลล์ (base64 ใหญ่ขึ้น ~1.37 เท่า)

/** แยกลิงก์ทีละบรรทัด -> { links, bad } (รับเฉพาะ http/https) */
export const parseLinks = (text) => {
  const lines = String(text || "").split(/\r?\n/).map((row) => row.trim()).filter(Boolean);
  const links = [];
  const bad = [];
  lines.forEach((line) => {
    try {
      const url = new URL(line);
      if (["http:", "https:"].includes(url.protocol)) links.push(url.href);
      else bad.push(line);
    } catch (e) {
      bad.push(line);
    }
  });
  return { links, bad };
};

/** ตรวจฟอร์มคำขอ — คืนข้อความ error ภาษาไทย หรือ "" ถ้าผ่าน */
export const validateRequestForm = ({ title, description, wantedAt, linksText, attachBytes = 0 }, today = new Date()) => {
  if (!String(title || "").trim()) return "กรุณากรอกหัวข้อ";
  if (String(title).trim().length > 120) return "หัวข้อยาวเกิน 120 ตัวอักษร";
  if (!String(description || "").trim()) return "กรุณากรอกรายละเอียดสิ่งที่ต้องการ";
  if (String(description).trim().length > 2000) return "รายละเอียดยาวเกิน 2,000 ตัวอักษร";
  const { links, bad } = parseLinks(linksText);
  if (bad.length) return `ลิงก์ไม่ถูกต้อง (ต้องขึ้นต้น http:// หรือ https://): ${bad[0]}`;
  if (links.length > LINK_MAX) return `แนบลิงก์ได้ไม่เกิน ${LINK_MAX} ลิงก์`;
  if (attachBytes > ATTACH_MAX_BYTES) return `ไฟล์แนบต้องไม่เกิน ${Math.round(ATTACH_MAX_BYTES / 1024)} KB (ไฟล์ใหญ่กว่านี้ให้แนบเป็นลิงก์)`;
  if (wantedAt) {
    const date = new Date(`${wantedAt}T00:00:00`);
    const start = new Date(today);
    start.setHours(0, 0, 0, 0);
    if (Number.isNaN(date.getTime())) return "วันที่ต้องการไม่ถูกต้อง";
    if (date < start) return "วันที่ต้องการต้องไม่ย้อนหลัง";
  }
  return "";
};
